import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { insureds, type Insured, type InsuredInput } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatCpfCnpj, formatDate } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Icon } from '@/shared/ui/Icon';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Pagination } from '@/shared/ui/Pagination';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/useToast';

import { PartyToolbar } from '../shared/PartyToolbar';
import { BlockInsuredDialog } from './BlockInsuredDialog';
import { InsuredForm } from './InsuredForm';
import { PERMISSAO_CRIAR, PERMISSAO_EDITAR, rotuloDoRamo } from './rotulos';

const situacoes = [
  { value: '', label: 'Todas as situacoes' },
  { value: 'ativo', label: 'Ativo' },
  { value: 'bloqueado', label: 'Bloqueado' },
];

const tipos = [
  { value: '', label: 'Todos os tipos' },
  { value: 'pj', label: 'Pessoa juridica' },
  { value: 'pf', label: 'Pessoa fisica' },
  { value: 'junta_comercial', label: 'Junta comercial' },
];

// O OnPoint mostra vinte por pagina.
const limite = 20;

/**
 * Lista de segurados (/segurados do OnPoint).
 *
 * As colunas sao CPF/CNPJ, Nome, Ramo, Bloqueado e Data bloqueio, com as acoes na
 * ultima coluna, como em Tomadores. O menu da linha tem Visualizar, que abre o detalhe, e
 * Bloquear, com o motivo; o segurado bloqueado oferece Desbloquear. "Filtros" abre
 * Nome do Segurado e CNPJ Segurado, com Limpar filtro e Aplicar filtro.
 */
export function InsuredsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [filtroNome, setFiltroNome] = useState('');
  const [filtroDocumento, setFiltroDocumento] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  // O filtro vale ao aplicar, como no OnPoint, e nao a cada tecla.
  const [aplicados, setAplicados] = useState({ name: '', document: '', kind: '' });
  const [criando, setCriando] = useState(false);
  const [bloqueando, setBloqueando] = useState<Insured | null>(null);
  const [desbloqueando, setDesbloqueando] = useState<Insured | null>(null);

  const podeCriar = can(PERMISSAO_CRIAR);
  const podeEditar = can(PERMISSAO_EDITAR);

  const filtros = { q: busca, status, ...aplicados };
  const comFiltro = Object.values(filtros).some((valor) => valor !== '');

  const consulta = useQuery({
    queryKey: ['insureds', { ...filtros, offset }],
    queryFn: ({ signal }) =>
      insureds.list({ ...filtros, limit: limite, offset }, signal),
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['insureds'] });
    void queryClient.invalidateQueries({ queryKey: ['insured'] });
  }

  const criar = useMutation({
    mutationFn: (input: InsuredInput) => insureds.create(input),
    onSuccess: (segurado) => {
      toast.success(`Segurado ${segurado.legal_name} cadastrado.`);
      setCriando(false);
      invalidar();
      void navigate(`/segurados/${segurado.id}`);
    },
  });

  const desbloquear = useMutation({
    mutationFn: (id: string) => insureds.unblock(id),
    onSuccess: (segurado) => {
      toast.success(`Segurado ${segurado.legal_name} desbloqueado.`);
      setDesbloqueando(null);
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const colunas: Column<Insured>[] = [
    {
      key: 'documento',
      header: 'CPF/CNPJ',
      render: (segurado) => (
        <span className="whitespace-nowrap tabular-nums">
          {formatCpfCnpj(segurado.document)}
        </span>
      ),
    },
    {
      key: 'nome',
      header: 'Nome',
      render: (segurado) => (
        <p className="truncate font-medium text-ink-900">{segurado.legal_name}</p>
      ),
    },
    {
      key: 'ramo',
      header: 'Ramo',
      render: (segurado) => rotuloDoRamo(segurado.sector),
    },
    {
      key: 'bloqueado',
      header: 'Bloqueado',
      render: (segurado) =>
        segurado.is_blocked ? <Badge tone="danger">Sim</Badge> : 'Nao',
    },
    {
      key: 'data-bloqueio',
      header: 'Data bloqueio',
      hideBelow: 'sm',
      render: (segurado) =>
        segurado.blocked_at ? (
          formatDate(segurado.blocked_at)
        ) : (
          <span className="text-ink-400">-</span>
        ),
    },
    {
      key: 'acoes',
      header: 'Acoes',
      align: 'right',
      render: (segurado) => (
        <div
          className="flex justify-end gap-1"
          onClick={(event) => event.stopPropagation()}
        >
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void navigate(`/segurados/${segurado.id}`)}
          >
            Visualizar
          </Button>
          {podeEditar && !segurado.is_blocked ? (
            <Button size="sm" variant="ghost" onClick={() => setBloqueando(segurado)}>
              <Icon name="lock" size={14} />
              Bloquear
            </Button>
          ) : null}
          {podeEditar && segurado.is_blocked ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setDesbloqueando(segurado)}
            >
              Desbloquear
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Segurados"
        description="Quem a garantia protege: o segurado, que tambem e o beneficiario."
        actions={
          podeCriar ? (
            <Button onClick={() => setCriando(true)}>
              <Icon name="shield" size={15} />
              Novo segurado
            </Button>
          ) : null
        }
      />

      {consulta.isError ? (
        <Alert tone="danger" title="Nao foi possivel carregar os segurados">
          {consulta.error.message}
        </Alert>
      ) : null}

      <Card>
        <PartyToolbar
          search={busca}
          onSearch={(valor) => {
            setBusca(valor);
            setOffset(0);
          }}
          placeholder="Buscar por razao social, nome fantasia, CPF ou CNPJ"
          filter={
            <Select
              label="Situacao"
              className="h-9"
              options={situacoes}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setOffset(0);
              }}
            />
          }
          extra={
            <div className="flex items-end gap-3 self-end">
              <Button
                size="sm"
                variant="secondary"
                aria-expanded={filtrosAbertos}
                onClick={() => setFiltrosAbertos((aberto) => !aberto)}
              >
                Filtros
              </Button>
              <span className="text-xs text-ink-600">
                {consulta.data?.total ?? 0}{' '}
                {(consulta.data?.total ?? 0) === 1 ? 'segurado' : 'segurados'}
              </span>
            </div>
          }
        />

        {filtrosAbertos ? (
          <form
            className="grid gap-3 border-b border-line px-5 py-4 sm:grid-cols-4"
            aria-label="Filtros"
            onSubmit={(event) => {
              event.preventDefault();
              setAplicados({
                name: filtroNome.trim(),
                document: filtroDocumento.trim(),
                kind: filtroTipo,
              });
              setOffset(0);
              setFiltrosAbertos(false);
            }}
          >
            <Field
              label="Nome do Segurado"
              placeholder="Digite o nome"
              value={filtroNome}
              onChange={(event) => setFiltroNome(event.target.value)}
            />
            <Field
              label="CNPJ Segurado"
              placeholder="Digite o CNPJ"
              value={filtroDocumento}
              onChange={(event) => setFiltroDocumento(event.target.value)}
            />
            <Select
              label="Tipo de segurado"
              options={tipos}
              value={filtroTipo}
              onChange={(event) => setFiltroTipo(event.target.value)}
            />
            <div className="flex items-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setFiltroNome('');
                  setFiltroDocumento('');
                  setFiltroTipo('');
                  setAplicados({ name: '', document: '', kind: '' });
                  setOffset(0);
                }}
              >
                Limpar filtro
              </Button>
              <Button type="submit">Aplicar filtro</Button>
            </div>
          </form>
        ) : null}

        <DataTable
          columns={colunas}
          items={consulta.data?.items ?? []}
          keyOf={(segurado) => segurado.id}
          loading={consulta.isPending}
          onRowClick={(segurado) => void navigate(`/segurados/${segurado.id}`)}
          empty={
            <EmptyState
              icon="shield"
              title={
                comFiltro ? 'Nenhum segurado encontrado' : 'Nenhum segurado cadastrado'
              }
              description={
                comFiltro
                  ? 'Ajuste a busca ou os filtros.'
                  : 'O segurado e quem a garantia protege, como o orgao publico ou a empresa contratante.'
              }
              action={
                podeCriar && !comFiltro ? (
                  <Button onClick={() => setCriando(true)}>Cadastrar segurado</Button>
                ) : null
              }
            />
          }
        />
        <Pagination
          total={consulta.data?.total ?? 0}
          limit={limite}
          offset={offset}
          onChange={setOffset}
        />
      </Card>

      <Modal
        open={criando}
        onClose={() => setCriando(false)}
        title="Novo segurado"
        size="lg"
      >
        <InsuredForm
          submitting={criar.isPending}
          error={criar.error}
          onCancel={() => setCriando(false)}
          onSubmit={(input) => criar.mutate(input)}
        />
      </Modal>

      <BlockInsuredDialog
        insured={bloqueando}
        onClose={() => setBloqueando(null)}
        onDone={() => {
          setBloqueando(null);
          invalidar();
        }}
      />

      <ConfirmDialog
        open={desbloqueando !== null}
        onClose={() => setDesbloqueando(null)}
        onConfirm={() =>
          desbloqueando ? desbloquear.mutate(desbloqueando.id) : undefined
        }
        title="Desbloquear o segurado?"
        message={`${desbloqueando?.legal_name ?? ''} volta a situacao ativa. O motivo do bloqueio fica no historico.`}
        confirmLabel="Desbloquear"
        loading={desbloquear.isPending}
      />
    </div>
  );
}
